import { fontFamilies } from "../styles/typography";
import { useEffect, useState } from "react";
import { Form, Input, App, Modal } from "antd";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  LOGIN_FAILED_FALLBACK,
  login,
  fetchUserInfo,
  showLicenseBlocked,
} from "../store/authSlice";
import { isLicenseBlockedDetail } from "../lib/licenseNotice";
import { useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  LockOutlined,
  UserOutlined,
  WarningOutlined,
  CloseOutlined,
  ArrowRightOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { getRestaurantName } from "../lib/restaurant";
import { APP_VERSION } from "../lib/version";
import type { ActiveUser } from "../api/auth";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { modalStyles } from "../utils/modalStyles";
import { useViewport } from "../hooks/useViewport";
import EdgeRestrictedNotice from "../components/EdgeRestrictedNotice";
import { useReloadOnNewVersion } from "../hooks/useReloadOnNewVersion";
import BrandLogo from "../components/BrandLogo";

const R = {
  bg: "var(--color-canvas)",
  surface: "var(--color-surface)",
  sidebarBg: "var(--color-sidebar)",
  border: "var(--color-shade-2)",
  borderMed: "var(--color-shade-3)",
  text: "var(--color-ink)",
  textSoft: "var(--color-body)",
  textMuted: "var(--color-muted)",
  ink: "var(--color-ink)",
  brand: "var(--color-brand)",
  brandDeep: "var(--color-brand-deep)",
  brandTint: "var(--color-brand-tint)",
  brandTint2: "var(--color-brand-tint2)",
  danger: "#DC2626",
  mono: fontFamilies.mono,
};

const FONT = fontFamilies.sans;

// Локални CSS override-и за да неутрализираме antd default-ите (син focus ring)
// и да приложим amber hover/focus върху Input и Input.Password в логин формата.
const FORM_STYLES = `
  .lts-login .ant-input-affix-wrapper,
  .lts-login .ant-input {
    border-radius: 10px !important;
    border-color: ${R.borderMed} !important;
    box-shadow: none !important;
    transition: border-color 120ms, box-shadow 120ms;
  }
  .lts-login .ant-input-affix-wrapper:hover,
  .lts-login .ant-input:hover {
    border-color: ${R.brand} !important;
  }
  .lts-login .ant-input-affix-wrapper-focused,
  .lts-login .ant-input-affix-wrapper:focus-within,
  .lts-login .ant-input:focus {
    border-color: ${R.brand} !important;
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--color-brand) 15%, transparent) !important;
  }
  .lts-login .ant-form-item-label > label {
    font-size: 11px !important;
    font-weight: 700 !important;
    letter-spacing: 1.2px !important;
    text-transform: uppercase;
    color: ${R.textMuted} !important;
  }
  .lts-login .ant-form-item-explain-error {
    font-size: 12px;
    margin-top: 4px;
  }
  .lts-login .ant-input-password-icon {
    color: ${R.textMuted};
  }
`;

export default function LoginPage() {
  const { t } = useTranslation();
  const { isNarrow, isMobile } = useViewport();
  const [loading, setLoading] = useState(false);
  const { message } = App.useApp();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const [showCapacityModal, setShowCapacityModal] = useState(false);
  const [activeUsers, setActiveUsers] = useState<ActiveUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [pendingCredentials, setPendingCredentials] = useState<{
    username: string;
    password: string;
  } | null>(null);
  const [showActiveSessionModal, setShowActiveSessionModal] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);

  // Изходът оставя касата тук: ако на сървъра има нова версия, презареждаме
  // тихо (docs/pwa.md). Не по време на вход или с отворен модал — там има
  // въведени данни и започнат поток.
  // Отвореният модал за лиценза („Достъпът е спрян") също е започнат поток —
  // тихото презареждане би го затворило преди „Разбрах".
  const licenseNoticePending = useAppSelector(
    (state) => state.auth.licenseNotice !== null,
  );
  useReloadOnNewVersion(
    !loading &&
      !showCapacityModal &&
      !showActiveSessionModal &&
      !licenseNoticePending,
  );

  async function onFinish(values: { username: string; password: string }) {
    setLoading(true);
    try {
      const restaurant_name = getRestaurantName();
      const result = await dispatch(
        login({ ...values, restaurant_name }),
      ).unwrap();

      if (result.hasActiveSession) {
        setPendingCredentials(values);
        setCurrentUserId(result.user_id);
        setShowActiveSessionModal(true);
        setLoading(false);
        return;
      }

      await dispatch(fetchUserInfo()).unwrap();
      queryClient.invalidateQueries({ queryKey: ["menu"] });
      message.success(t("login.loginSuccess"));
      navigate("/", { replace: true });
    } catch (err: unknown) {
      const error = err as {
        response?: {
          status?: number;
          data?: { detail?: { message?: string; active_users?: ActiveUser[] } };
        };
      };

      if (error.response?.status === 409 && error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (detail.active_users && detail.active_users.length > 0) {
          setActiveUsers(detail.active_users);
          setPendingCredentials(values);
          setShowCapacityModal(true);
          setLoading(false);
          return;
        }
      }
      // Гратисният период на лиценза е минал (403 `license_expired`) — не
      // „Неуспешен вход", а модалът, който казва защо (LicenseNoticeHost).
      if (isLicenseBlockedDetail(err)) {
        dispatch(showLicenseBlocked(err));
        return;
      }
      // Текстът на сървъра е единственото обяснение — общото съобщение би
      // скрило причината. Облакът казва колко опита остават до паузата на IP
      // адреса, колко трае паузата (429, BE `login_ip_attempts.py`) и кога
      // акаунтът е деактивиран (6 грешни за 24 ч., BE `login_attempts.py`); касата в
      // локален режим — неактивен потребител или „Твърде много опити" след 5
      // грешни за 15 минути. Текстът е на български и при английски интерфейс
      // (BE не го превежда). Thunk-ът връща `detail`-а като низ, затова се
      // чете оттам. Отказ БЕЗ тяло (мрежова грешка, HTML от ingress) няма
      // текст на сървъра: там thunk-ът е сложил `LOGIN_FAILED_FALLBACK` и той
      // не бива да се показва, защото е непреведен английски низ.
      const serverMessage =
        typeof err === "string" && err.trim() !== LOGIN_FAILED_FALLBACK
          ? err.trim()
          : "";
      message.error(serverMessage || t("login.loginFailed"));
    } finally {
      setLoading(false);
    }
  }

  async function handleForceLogin() {
    if (!selectedUserId || !pendingCredentials) return;
    setLoading(true);
    try {
      const restaurant_name = getRestaurantName();
      await dispatch(
        login({
          ...pendingCredentials,
          restaurant_name,
          force_logout_user_id: selectedUserId,
        }),
      ).unwrap();
      await dispatch(fetchUserInfo()).unwrap();
      queryClient.invalidateQueries({ queryKey: ["menu"] });
      message.success(t("login.loginSuccess"));
      setShowCapacityModal(false);
      setActiveUsers([]);
      setSelectedUserId(null);
      setPendingCredentials(null);
      navigate("/", { replace: true });
    } catch {
      message.error(t("login.loginFailed"));
    } finally {
      setLoading(false);
    }
  }

  function handleCancelModal() {
    setShowCapacityModal(false);
    setActiveUsers([]);
    setSelectedUserId(null);
    setPendingCredentials(null);
  }

  function handleCancelActiveSessionModal() {
    setShowActiveSessionModal(false);
    setCurrentUserId(null);
    setPendingCredentials(null);
  }

  async function handleForceLogoutAndLogin() {
    if (!currentUserId || !pendingCredentials) return;
    setLoading(true);
    try {
      const restaurant_name = getRestaurantName();
      await dispatch(
        login({
          ...pendingCredentials,
          restaurant_name,
          force_logout_user_id: currentUserId,
        }),
      ).unwrap();
      await dispatch(fetchUserInfo()).unwrap();
      queryClient.invalidateQueries({ queryKey: ["menu"] });
      message.success(t("login.loginSuccess"));
      setShowActiveSessionModal(false);
      setCurrentUserId(null);
      setPendingCredentials(null);
      navigate("/", { replace: true });
    } catch {
      message.error(t("login.loginFailed"));
    } finally {
      setLoading(false);
    }
  }

  function formatDateTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const dateOnly = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
    );
    const timeStr = date.toLocaleString("bg-BG", {
      hour: "2-digit",
      minute: "2-digit",
    });
    if (dateOnly.getTime() === today.getTime())
      return `${t("common.today")} ${timeStr}`;
    if (dateOnly.getTime() === yesterday.getTime())
      return `${t("common.yesterday")} ${timeStr}`;
    return date.toLocaleString("bg-BG", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  return (
    <div
      className="lts-login"
      style={{
        minHeight: "100vh",
        display: "grid",
        gridTemplateColumns: isNarrow ? "1fr" : "1.05fr 1fr",
        background: R.bg,
        fontFamily: FONT,
        color: R.text,
        position: "relative",
      }}
    >
      <style>{FORM_STYLES}</style>

      {/* Language switcher — top right */}
      <div
        style={{
          position: "absolute",
          top: isNarrow ? 12 : 20,
          right: isNarrow ? 12 : 24,
          zIndex: 50,
        }}
      >
        <LanguageSwitcher />
      </div>

      {/* Hero panel — hidden on narrow */}
      {!isNarrow && (
        <div
          style={{
            position: "relative",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: R.sidebarBg,
            borderRight: `1px solid ${R.border}`,
            padding: "48px",
          }}
        >
          {/* Decorative amber glows */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              background: `
                radial-gradient(ellipse 55% 50% at 15% 15%, ${R.brandTint} 0%, transparent 60%),
                radial-gradient(ellipse 70% 55% at 85% 90%, ${R.brandTint2} 0%, transparent 65%)
              `,
              pointerEvents: "none",
            }}
          />
          {/* Subtle grid overlay */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              backgroundImage: `linear-gradient(${R.border} 1px, transparent 1px), linear-gradient(90deg, ${R.border} 1px, transparent 1px)`,
              backgroundSize: "48px 48px",
              maskImage:
                "radial-gradient(ellipse 70% 60% at 50% 50%, #000 30%, transparent 80%)",
              opacity: 0.6,
              pointerEvents: "none",
            }}
          />

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: "easeOut" }}
            style={{
              position: "relative",
              zIndex: 1,
              maxWidth: 460,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 28,
            }}
          >
            {/* Brand mark — уголемен, мащабира се плавно между таблет (hero
                се показва от 640px) и широк десктоп чрез clamp; версията е
                отделно, центрирана в дъното на панела. Иконата вляво, вдясно
                словомарката („Soft" в бранд-оранжевото) и под нея слоганът в
                моно шрифт — същият бранд-блок стои и на ПИН екрана. */}
            <div className="flex items-center gap-[clamp(10px,1.1vw,16px)]">
              <BrandLogo className="h-[clamp(58px,6.4vw,92px)] w-[clamp(58px,6.4vw,92px)]" />
              <div className="flex flex-col gap-[clamp(4px,0.45vw,7px)]">
                <div className="text-[length:clamp(28px,3.1vw,44px)] font-bold leading-none tracking-[-1.4px] text-ink">
                  Unreal<span className="text-brand-ink">Soft</span>
                </div>
                <div className="font-pos-mono text-[length:clamp(10px,0.85vw,12px)] font-medium uppercase leading-none tracking-[2.4px] text-muted">
                  {t("login.eyebrow")}
                </div>
              </div>
            </div>

            {/* Hero headline */}
            <div>
              {/* Стъпка под бранда в йерархията — брандът е най-едрият текст */}
              <h1 className="m-0 text-[length:clamp(24px,2.5vw,36px)] font-bold leading-[1.15] tracking-[-1px] text-ink">
                {t("login.heroHeadline")}
              </h1>
              <p
                style={{
                  margin: "14px 0 0",
                  fontSize: 15,
                  lineHeight: 1.55,
                  color: R.textSoft,
                  maxWidth: 420,
                }}
              >
                {t("login.heroDescription")}
              </p>
            </div>

            {/* Feature bullets */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: 10,
                marginTop: 4,
              }}
            >
              {[
                t("login.feature1"),
                t("login.feature2"),
                t("login.feature3"),
              ].map((feat, i) => (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    fontSize: 13,
                    color: R.textSoft,
                    fontWeight: 500,
                  }}
                >
                  <span
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 6,
                      background: R.brandTint,
                      color: "var(--color-brand-deep-ink)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 11,
                      fontWeight: 700,
                    }}
                  >
                    ✓
                  </span>
                  {feat}
                </div>
              ))}

              {/* Версията — центрирана спрямо ширината на списъка с отметки */}
              <div className="mt-2 text-center font-pos-mono text-[11px] font-bold uppercase tracking-[1.4px] text-muted">
                v{APP_VERSION}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Form panel — на телефон съдържанието е закотвено отгоре (не
          центрирано), за да не „виси" надолу с празно място над бранда */}
      <div
        style={{
          display: "flex",
          alignItems: isNarrow ? "flex-start" : "center",
          justifyContent: "center",
          padding: isNarrow ? "60px 18px 32px" : "32px",
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: "easeOut" }}
          style={{ width: "100%", maxWidth: 420 }}
        >
          {/* On narrow: show brand mark above the card — вертикално (лого над
              текста), за да е всичко на една централна ос с името на
              ресторанта и картата; хоризонталният ред изглежда изместен. */}
          {isNarrow && (
            <>
              {/* Лого над словомарката („Soft" в бранд-оранжевото), под нея
                  слоганът в моно шрифт — всичко на една централна ос */}
              <div className="mb-7 flex flex-col items-center gap-3">
                <BrandLogo className="h-[72px] w-[72px]" />
                <div className="flex flex-col items-center gap-[6px]">
                  <span className="text-[26px] font-bold leading-none tracking-[-1px] text-ink">
                    Unreal<span className="text-brand-ink">Soft</span>
                  </span>
                  <span className="font-pos-mono text-[10px] font-medium uppercase leading-none tracking-[2.4px] text-muted">
                    {t("login.eyebrow")}
                  </span>
                </div>
              </div>
              {/* Името на заведението — центрирано над формата, в черно,
                  с размера на етикетите „Добре дошли"/„Потребител" */}
              <div className="mb-3 truncate text-center text-[11px] font-bold uppercase leading-none tracking-[1.2px] text-ink">
                {getRestaurantName()}
              </div>
            </>
          )}

          {/* Restaurant name pill — above the card (на телефон името е в
              бранд-реда до UnrealSoft, за да не се дублира) */}
          {!isNarrow && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: 12,
              }}
            >
              <div
                style={{
                  color: "var(--color-brand-deep-ink)",
                  fontSize: 12,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                }}
              >
                {getRestaurantName()}
              </div>
            </div>
          )}

          {/* Ограничен локален режим (§14.3.1, docs/edge.md): касата приема
              само този вход — лентата казва защо терминалът е тук. */}
          <EdgeRestrictedNotice className="mb-3" />

          <div
            style={{
              background: R.surface,
              border: `1px solid ${R.border}`,
              borderRadius: 16,
              padding: isNarrow ? "22px 20px" : "32px 32px 28px",
            }}
          >
            {/* Header */}
            <div style={{ marginBottom: 22 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: 1.2,
                  textTransform: "uppercase",
                  color: R.textMuted,
                  marginBottom: 8,
                }}
              >
                {t("login.welcomeKicker")}
              </div>
              <h2
                style={{
                  margin: 0,
                  fontSize: isNarrow ? 22 : 26,
                  fontWeight: 700,
                  letterSpacing: -0.6,
                  color: R.ink,
                  lineHeight: 1.15,
                }}
              >
                {t("login.title")}
              </h2>
              <p
                style={{
                  margin: "6px 0 0",
                  fontSize: 13,
                  color: R.textSoft,
                  lineHeight: 1.45,
                }}
              >
                {t("login.subtitle")}
              </p>
            </div>

            <Form
              layout="vertical"
              onFinish={onFinish}
              requiredMark={false}
              autoComplete="off"
            >
              <Form.Item
                label={t("login.usernameLabel")}
                name="username"
                rules={[
                  { required: true, message: t("login.usernameRequired") },
                  { min: 3, message: t("login.usernameMin") },
                ]}
              >
                <Input
                  size="large"
                  placeholder={t("login.usernamePlaceholder")}
                  prefix={
                    <UserOutlined
                      style={{ color: R.textMuted, fontSize: 14 }}
                    />
                  }
                  style={{
                    height: isMobile ? 48 : 44,
                    fontSize: 14,
                    color: R.ink,
                  }}
                />
              </Form.Item>

              <Form.Item
                label={t("login.passwordLabel")}
                name="password"
                rules={[
                  { required: true, message: t("login.passwordRequired") },
                  { min: 3, message: t("login.passwordMin") },
                ]}
              >
                <Input.Password
                  size="large"
                  placeholder={t("login.passwordPlaceholder")}
                  prefix={
                    <LockOutlined
                      style={{ color: R.textMuted, fontSize: 14 }}
                    />
                  }
                  style={{
                    height: isMobile ? 48 : 44,
                    fontSize: 14,
                    color: R.ink,
                  }}
                />
              </Form.Item>

              <motion.button
                whileTap={{ scale: 0.985 }}
                type="submit"
                disabled={loading}
                style={{
                  width: "100%",
                  height: isMobile ? 48 : 46,
                  marginTop: 4,
                  borderRadius: 10,
                  border: "none",
                  background: loading ? R.brandDeep : R.brand,
                  color: "#fff",
                  fontSize: 14,
                  fontWeight: 700,
                  letterSpacing: 0.2,
                  cursor: loading ? "wait" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  transition: "background 120ms, transform 80ms",
                  fontFamily: FONT,
                }}
                onMouseEnter={(e) => {
                  if (!loading) e.currentTarget.style.background = R.brandDeep;
                }}
                onMouseLeave={(e) => {
                  if (!loading) e.currentTarget.style.background = R.brand;
                }}
              >
                {loading ? (
                  <span
                    style={{
                      width: 16,
                      height: 16,
                      borderRadius: 999,
                      border: "2px solid rgba(255,255,255,0.4)",
                      borderTopColor: "#fff",
                      animation: "lts-spin 0.7s linear infinite",
                    }}
                  />
                ) : (
                  <>
                    {t("login.loginButton")}
                    <ArrowRightOutlined style={{ fontSize: 12 }} />
                  </>
                )}
              </motion.button>
              <style>{`@keyframes lts-spin { to { transform: rotate(360deg); } }`}</style>

              <div
                style={{
                  marginTop: 20,
                  paddingTop: 16,
                  borderTop: `1px solid ${R.border}`,
                  textAlign: "center",
                  fontSize: 11,
                  fontWeight: 500,
                  letterSpacing: 0.3,
                  color: R.textMuted,
                  fontFamily: R.mono,
                }}
              >
                {t("common.securedJwt")}
              </div>
            </Form>
          </div>
        </motion.div>
      </div>

      {/* Active session modal */}
      <SessionChoiceModal
        open={showActiveSessionModal}
        onClose={handleCancelActiveSessionModal}
        onConfirm={handleForceLogoutAndLogin}
        loading={loading}
        title={t("login.activeSession")}
        subtitle={t("login.activeSessionDesc")}
        confirmLabel={t("login.terminateAndLogin")}
        cancelLabel={t("common.cancel")}
        isMobile={isMobile}
        isNarrow={isNarrow}
      />

      {/* Capacity (insufficient licenses) modal */}
      <Modal
        open={showCapacityModal}
        onCancel={handleCancelModal}
        footer={null}
        width={isMobile ? undefined : 640}
        centered
        closable={false}
        destroyOnHidden
        styles={modalStyles}
        style={{ maxWidth: "calc(100vw - 32px)" }}
      >
        <div style={{ background: R.surface, fontFamily: FONT }}>
          {/* Header */}
          <div
            style={{
              padding: isMobile ? "14px 16px" : "20px 24px",
              borderBottom: `1px solid ${R.border}`,
              display: "flex",
              alignItems: "flex-start",
              gap: 12,
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: R.brandTint,
                color: "var(--color-brand-deep-ink)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <TeamOutlined style={{ fontSize: 20 }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  letterSpacing: -0.3,
                  color: R.ink,
                  lineHeight: 1.2,
                }}
              >
                {t("login.insufficientLicenses")}
              </div>
              <div
                style={{
                  marginTop: 4,
                  fontSize: 13,
                  color: R.textSoft,
                  lineHeight: 1.4,
                }}
              >
                {t("login.selectUserToLogout")}
              </div>
            </div>
            <button
              onClick={handleCancelModal}
              style={{
                height: 30,
                width: 30,
                borderRadius: 6,
                background: "transparent",
                border: "none",
                color: R.textMuted,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "var(--color-shade-1)";
                e.currentTarget.style.color = R.ink;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = R.textMuted;
              }}
            >
              <CloseOutlined style={{ fontSize: 14 }} />
            </button>
          </div>

          {/* Body */}
          <div style={{ padding: isNarrow ? "16px" : "20px 24px" }}>
            <div
              style={{
                maxHeight: 360,
                overflowY: "auto",
                display: "grid",
                gridTemplateColumns: isNarrow
                  ? "1fr"
                  : "repeat(2, minmax(0, 1fr))",
                gap: 10,
              }}
            >
              {activeUsers.map((user) => {
                const isSelected = selectedUserId === user.user_id;
                return (
                  <button
                    type="button"
                    key={user.user_id}
                    onClick={() => setSelectedUserId(user.user_id)}
                    style={{
                      textAlign: "left",
                      padding: "12px 14px",
                      borderRadius: 10,
                      border: `1px solid ${isSelected ? R.brand : R.border}`,
                      background: isSelected ? R.brandTint : R.surface,
                      cursor: "pointer",
                      transition: "border-color 120ms, background 120ms",
                      fontFamily: FONT,
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected)
                        e.currentTarget.style.borderColor = R.borderMed;
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected)
                        e.currentTarget.style.borderColor = R.border;
                    }}
                  >
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: R.ink,
                        letterSpacing: -0.2,
                      }}
                    >
                      {user.username}
                    </div>
                    <div
                      style={{
                        marginTop: 4,
                        fontSize: 11,
                        fontWeight: 500,
                        color: R.textMuted,
                        fontFamily: R.mono,
                      }}
                    >
                      {t("login.lastActivity")}{" "}
                      {formatDateTime(user.last_activity_at)}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div
            style={{
              padding: isMobile ? "12px 16px" : "14px 24px",
              borderTop: `1px solid ${R.border}`,
              display: "flex",
              flexDirection: isMobile ? "column-reverse" : "row",
              justifyContent: "flex-end",
              gap: isMobile ? 8 : 10,
            }}
          >
            <GhostButton
              onClick={handleCancelModal}
              label={t("common.cancel")}
              fullWidth={isMobile}
            />
            <PrimaryButton
              onClick={handleForceLogin}
              loading={loading}
              disabled={!selectedUserId}
              label={t("login.forceLogout")}
              fullWidth={isMobile}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}

/* ---------- small shared modal bits (local to file) ---------- */

function SessionChoiceModal({
  open,
  onClose,
  onConfirm,
  loading,
  title,
  subtitle,
  confirmLabel,
  cancelLabel,
  isMobile,
  isNarrow,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  loading: boolean;
  title: string;
  subtitle: string;
  confirmLabel: string;
  cancelLabel: string;
  isMobile: boolean;
  isNarrow: boolean;
}) {
  // ESC closes (antd default via onCancel handles backdrop + esc)
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // preventDefault: без него същото натискане минава и през глобалния
        // Esc-„назад" на SharedAccountGate (правило от CLAUDE.md)
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={isMobile ? undefined : 440}
      centered
      closable={false}
      destroyOnHidden
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div style={{ background: R.surface, fontFamily: FONT }}>
        <div
          style={{
            padding: isMobile ? "14px 16px" : "20px 24px",
            borderBottom: `1px solid ${R.border}`,
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: R.brandTint,
              color: "var(--color-brand-deep-ink)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <WarningOutlined style={{ fontSize: 20 }} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 18,
                fontWeight: 700,
                letterSpacing: -0.3,
                color: R.ink,
                lineHeight: 1.2,
              }}
            >
              {title}
            </div>
            <div
              style={{
                marginTop: 4,
                fontSize: 13,
                color: R.textSoft,
                lineHeight: 1.45,
              }}
            >
              {subtitle}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              height: 30,
              width: 30,
              borderRadius: 6,
              background: "transparent",
              border: "none",
              color: R.textMuted,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "var(--color-shade-1)";
              e.currentTarget.style.color = R.ink;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
              e.currentTarget.style.color = R.textMuted;
            }}
          >
            <CloseOutlined style={{ fontSize: 14 }} />
          </button>
        </div>

        <div
          style={{
            padding: isNarrow ? "14px 16px" : "16px 24px 20px",
            display: "flex",
            flexDirection: isMobile ? "column-reverse" : "row",
            justifyContent: "flex-end",
            gap: isMobile ? 8 : 10,
          }}
        >
          <GhostButton
            onClick={onClose}
            label={cancelLabel}
            fullWidth={isMobile}
          />
          <PrimaryButton
            onClick={onConfirm}
            loading={loading}
            label={confirmLabel}
            fullWidth={isMobile}
          />
        </div>
      </div>
    </Modal>
  );
}

function PrimaryButton({
  onClick,
  loading,
  disabled,
  fullWidth,
  label,
}: {
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  label: string;
}) {
  const isDisabled = disabled || loading;
  return (
    <button
      onClick={onClick}
      disabled={isDisabled}
      style={{
        height: 42,
        padding: fullWidth ? 0 : "0 18px",
        width: fullWidth ? "100%" : undefined,
        borderRadius: 10,
        border: "none",
        background: isDisabled ? "color-mix(in srgb, var(--color-brand) 55%, transparent)" : R.brand,
        color: "#fff",
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: 0.2,
        cursor: isDisabled ? "not-allowed" : "pointer",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        fontFamily: FONT,
        transition: "background 120ms",
      }}
      onMouseEnter={(e) => {
        if (!isDisabled) e.currentTarget.style.background = R.brandDeep;
      }}
      onMouseLeave={(e) => {
        if (!isDisabled) e.currentTarget.style.background = R.brand;
      }}
    >
      {loading && (
        <span
          style={{
            width: 14,
            height: 14,
            borderRadius: 999,
            border: "2px solid rgba(255,255,255,0.4)",
            borderTopColor: "#fff",
            animation: "lts-spin 0.7s linear infinite",
          }}
        />
      )}
      {label}
    </button>
  );
}

function GhostButton({
  onClick,
  fullWidth,
  label,
}: {
  onClick: () => void;
  fullWidth?: boolean;
  label: string;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        height: 42,
        padding: fullWidth ? 0 : "0 18px",
        width: fullWidth ? "100%" : undefined,
        borderRadius: 10,
        border: `1px solid ${R.border}`,
        background: "transparent",
        color: R.textSoft,
        fontSize: 13,
        fontWeight: 600,
        cursor: "pointer",
        fontFamily: FONT,
        transition: "background 120ms, color 120ms",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "var(--color-shade-1)";
        e.currentTarget.style.color = R.ink;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.color = R.textSoft;
      }}
    >
      {label}
    </button>
  );
}
